import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from './SEO';
import Header from './Header';
import { trackAppStoreCtaClick } from '../utils/analytics';
import '../App.css';

const comedogenicIngredients = [
    "Acetylated Lanolin", "Acetylated Lanolin Alcohol", "Active Soil Complex", "Ahnfeltia Concinna", "Alaria Esculenta", "Algae Extract", "Algin", "Argan Oil", "Argania Spinosa", "Ascophyllum Nodosum", "Avocado Oil", "Bismuth Oxychloride", "Black Kelp", "Bladderwack", "Blue Algae", "Blue Green Algae", "Brown Algae", "Butyl Stearate", "Carrageenan", "Carrageenan Moss", "Cetyl Acetate", "Cetearyl Alcohol & Ceteareth 20", "Chlorella", "Chondrus Crispus", "Coal Tar", "Cocoa Butter", "Coconut Alkanes", "Coconut Butter", "Cocos Nucifera (Coconut) Oil", "Colloidal Sulfur", "Corallina Officinalis", "Cotton Awws", "Cotton Seed Oil", "Crithmum Maritimum", "D&C Red #3, #17, #21, #30, #36", "Decyl Oleate", "Dilsea Carnosa", "Dioctyl Succinate", "Disodium Monooleamido PEG 2-Sulfosuccinate", "Dulse", "Ecklonia", "Enteromorpha Compressa", "Ethoxylated Lanolin", "Ethylhexyl Palmitate", "Evening Primrose Oil", "Fucus Vesiculosus", "Glyceryl Stearate SE", "Glyceryl 3-Diisostearate", "Grapeseed Oil", "Haslea Ostrearia", "Himanthalia Elongate", "Hydrogenated Vegetable Oil", "Hydrolyzed Lola Implexa", "Hydrolyzed Rhodophycea", "Hypnea Musciformis", "Irish Moss", "Isocetyl Alcohol", "Isocetyl Stearate", "Isodecyl Oleate", "Isopropyl Isosterate", "Isopropyl Linolate", "Isopropyl Linoleate", "Isopropyl Myristate", "Isopropyl Palmitate", "Isostearyl Isostearate", "Isostearyl Neopentanoate", "Kelp", "Laminaria Digitata", "Laminaria Longicruris", "Laminaria Saccharine", "Lanolin", "Lanolin Alcohol", "Laureth-4", "Laureth-23", "Lauric Acid", "Lauryl Alcohol", "Linseed Oil", "Lithothamnium Calcareum", "Lola Implexa", "Marine Algae", "Mastocarpus Stellatus", "Mineral Oil", "Mink Oil", "Myreth 3 Myristate", "Myristic Acid", "Myristyl Lactate", "Myristyl Myristate", "Norwegian Kelp", "Octyl Palmitate", "Octyl Stearate", "Oleth-3", "Oleth-5", "Oleyl Alcohol", "Padina Pavonica", "Palmaria Palmate", "Peanut Oil", "PEG 16 Lanolin", "PEG 200 Dilaurate", "Petroleum", "PG Monostearate", "Phytessence Wakame", "Plankton", "Porphyra", "Porphyridium Cruentum", "PPG 2 Myristyl Propionate", "PPG 6 Ceteth 10 Phosphate", "PPG 10 Cetyl Ether", "Polyglyceryl-3 Diisostearate", "Potassium Chloride", "Propylene Glycol Monostearate", "Red Algae", "Rockweed", "Rosehip Oil", "Sargassum", "Seaweed", "Sea Whip", "Sesame Oil", "Shark Liver Oil", "Shea Butter", "Sodium Laureth Sulfate", "Sodium Lauryl Sulfate", "Soil Minerals", "Solulan 16", "Sorbitan Oleate", "Soybean Oil", "Spirulina", "Steareth 10", "Stearic Acid TEA", "Stearyl Heptanoate", "Sulfated Castor Oil", "Sulfated Jojoba Oil", "Ulva Lactuca", "Undaria", "Wakame", "Wheat Germ Glyceride", "Wheat Germ Oil", "Xylene"
];

function tryExactMatchWithPosition(text, ingredient, originalIngredient, matchedPositions) {
  const patterns = [ingredient];

  if (ingredient.includes("&")) {
    patterns.push(ingredient.replace(/&/g, " & "));
    patterns.push(ingredient.replace(/ & /g, "&"));
  }

  for (const pattern of patterns) {
    const isSingleWord = !pattern.includes(" ") && !pattern.includes("&") && !pattern.includes("-");
    
    const escapeRegex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const escapedPattern = escapeRegex(pattern);
    
    let regexPattern;
    if (isSingleWord) {
      regexPattern = `(?<!\\w)${escapedPattern}(?!\\w)`;
    } else {
      regexPattern = `\\b${escapedPattern}\\b`;
    }

    try {
      const regex = new RegExp(regexPattern, 'i');
      const match = text.match(regex);
      if (match) {
        const start = match.index;
        const end = start + match[0].length;
        
        // Check if this match overlaps with already matched positions
        let overlaps = false;
        for (let i = start; i < end; i++) {
          if (matchedPositions.has(i)) {
            overlaps = true;
            break;
          }
        }
        
        return { found: true, start, end, overlaps };
      }
    } catch (e) {
      // Fallback to simple include if regex fails
      const index = text.indexOf(pattern);
      if (index !== -1) {
        const start = index;
        const end = start + pattern.length;
        
        // Check if this match overlaps with already matched positions
        let overlaps = false;
        for (let i = start; i < end; i++) {
          if (matchedPositions.has(i)) {
            overlaps = true;
            break;
          }
        }
        
        return { found: true, start, end, overlaps };
      }
    }
  }

  return { found: false, start: 0, end: 0, overlaps: false };
}

function trySpecialPatternsWithPosition(text, ingredient, originalIngredient, matchedPositions) {
  if (ingredient.includes("d&c red")) {
    const flexiblePattern = /d\s*&\s*c\s*red\s*#\s*[0-9,\s]+/i;
    const match = text.match(flexiblePattern);
    if (match) {
      const start = match.index;
      const end = start + match[0].length;
      
      // Check if this match overlaps with already matched positions
      let overlaps = false;
      for (let i = start; i < end; i++) {
        if (matchedPositions.has(i)) {
          overlaps = true;
          break;
        }
      }
      
      return { found: true, start, end, overlaps };
    }
  }

  return { found: false, start: 0, end: 0, overlaps: false };
}

export const homeFaqs = [
  {
    question: 'What is the best skincare ingredient checker?',
    answer:
      "Acne Safe is a free ingredient checker built specifically for acne-prone skin. Scan a product label with your phone camera or paste the ingredient list, and it instantly flags any of 150+ known comedogenic (pore-clogging) ingredients — no chemistry background required.",
  },
  {
    question: 'Is there a comedogenic ingredient scanner app?',
    answer:
      "Yes. Acne Safe's camera scanner reads any product's ingredient label and checks it against a database of comedogenic ingredients in under a second, so you know before you buy whether a product is likely to clog pores.",
  },
  {
    question: 'What does an acne-safe app actually do?',
    answer:
      "An acne-safe app analyzes a skincare or makeup product's ingredient list and tells you whether it contains ingredients known to clog pores and trigger breakouts. Acne Safe does this through a camera scanner, ingredient paste tool, a searchable product database, and a Chrome extension for shopping online.",
  },
  {
    question: 'How do I find pore-clogging ingredients in a product before I buy it?',
    answer:
      "Paste the ingredient list into the tool on this page, scan the label with the Acne Safe app, or search our product database — any of the three will show you exactly which ingredients, if any, are known to clog pores.",
  },
];

const homeStructuredData = [
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Acne Safe',
    url: 'https://www.useacnesafe.com',
    logo: 'https://www.useacnesafe.com/logo512.png',
    sameAs: [
      'https://apps.apple.com/us/app/acne-safe-ingredient-scanner/id6747936538',
      'https://chromewebstore.google.com/detail/acnesafe/jfphllcnckidjfjploadnmbkljnjlmhh',
      'https://www.tiktok.com/@acnesafe',
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Acne Safe Ingredient Scanner',
    applicationCategory: 'HealthApplication',
    operatingSystem: 'iOS',
    url: 'https://www.useacnesafe.com',
    description:
      'Scan skincare ingredient labels to instantly check for comedogenic, pore-clogging ingredients before you buy.',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: homeFaqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  },
];

function Home() {
  const [ingredients, setIngredients] = useState('');
  const [result, setResult] = useState(null);

  // Clear result when ingredients text changes
  useEffect(() => {
    if (result) {
      setResult(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ingredients]);

  const handleAnalyze = () => {
    const lowercasedText = ingredients.toLowerCase();
    const foundWords = [];
    const matchedPositions = new Set(); // Track positions that have been matched

    // Sort ingredients by length (longest first) to match most specific first
    const sortedIngredients = [...comedogenicIngredients].sort((a, b) => b.length - a.length);

    for (const ingredient of sortedIngredients) {
      const lowercasedIngredient = ingredient.toLowerCase();
      
      // Check if this ingredient matches and if it overlaps with already matched positions
      let matchInfo = tryExactMatchWithPosition(lowercasedText, lowercasedIngredient, ingredient, matchedPositions);

      if (!matchInfo.found) {
        matchInfo = trySpecialPatternsWithPosition(lowercasedText, lowercasedIngredient, ingredient, matchedPositions);
      }

      if (matchInfo.found && !matchInfo.overlaps) {
        // Mark all positions in this match as used
        for (let i = matchInfo.start; i < matchInfo.end; i++) {
          matchedPositions.add(i);
        }
        foundWords.push(ingredient);
      }
    }

    const uniqueFoundWords = [...new Set(foundWords)];

    if (uniqueFoundWords.length > 0) {
      setResult({
        foundIngredients: uniqueFoundWords,
        message: `⚠️ Found ${uniqueFoundWords.length} potentially comedogenic ingredient(s):`,
        isComedogenic: true
      });
    } else {
      setResult({
        foundIngredients: [],
        message: '✓ No comedogenic ingredients detected. This product may be safe for acne-prone skin.',
        isComedogenic: false
      });
    }
  };

  return (
    <div className="App">
      <SEO 
        title="Acne Safe | Ingredient Scanner"
        description="Scan skincare ingredients to check if products are safe for acne-prone skin. Instant ingredient analysis powered by AI. Check for comedogenic ingredients before you buy."
      />
      {homeStructuredData.map((schema) => (
        <script
          key={schema['@type']}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}
      <Header />
      
      <main className="app-main">
        {/* Hero Section */}
        <section className="hero">
          <div className="hero-content">
            <div className="hero-text">
              <h2>Stop buying products that break you out</h2>
              <p>Scan any product's ingredients and instantly see what clogs pores, so you only use what keeps your skin clear.</p>
              <a href="https://apps.apple.com/us/app/acne-safe-ingredient-scanner/id6747936538" target="_blank" rel="noopener noreferrer" className="cta-button" onClick={() => trackAppStoreCtaClick('home_hero')}>
                <img src="/download.png" alt="Download on App Store" className="download-button-image" />
              </a>
              <p className="hero-conversion-note">Try it before your next Ulta, Sephora, or Target run.</p>
            </div>
            <div className="hero-image">
              <img 
                src="/hero-image.webp"
                alt="Acne Safe App Preview" 
                className="hero-phone-image"
                fetchPriority="high"
                loading="eager"
                width="600"
                height="800"
              />
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="how-it-works">
          <h2 className="section-title">How It Works</h2>
          <div className="steps">
            <div className="step">
              <div className="step-number">1</div>
              <h3>Scan or Paste</h3>
              <p>Point your camera at any product label or paste the ingredient list. Works on skincare, makeup, haircare, and SPF.</p>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <h3>We Flag the Bad Stuff</h3>
              <p>Every ingredient is checked against 150+ known pore-clogging ingredients in under a second.</p>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <h3>Buy with Confidence</h3>
              <p>See exactly which ingredients are safe and which ones could cause breakouts before you spend a dollar.</p>
            </div>
          </div>
        </section>

        {/* Advantages Section */}
        <section className="advantages-section">
          <h2 className="section-title">Everything you need to shop smarter</h2>
          <div className="advantages-content">
            <div className="advantages-image">
              <img 
                src="/app-phone-screenshot.webp"
                alt="Acne Safe App" 
                className="advantages-phone-image"
              />
            </div>
            <div className="advantages-list">
              <div className="advantage-item">
                <h3>Camera Scanner</h3>
                <p>Snap a photo of any ingredient label in-store or at home. No typing, no searching — just point and scan.</p>
              </div>
              <div className="advantage-item">
                <h3>Product Search</h3>
                <p>Browse tens of thousands of Dermstore, Sephora, Ulta, Target, and Walgreens products already analyzed and rated for acne safety.</p>
              </div>
              <div className="advantage-item">
                <h3>Ingredient Breakdown</h3>
                <p>See exactly which ingredients are flagged and why, so you learn what to look for over time.</p>
              </div>
              <div className="advantage-item">
                <h3>Chrome Extension</h3>
                <p>Shopping online? Our browser extension checks ingredients on product pages automatically while you browse.</p>
              </div>
            </div>
          </div>
        </section>

        {/* What does Acne Safe include */}
        <section className="why-choose">
          <div className="why-choose-content">
            <h2 className="section-title">Why people switch to Acne Safe</h2>
            <p className="why-choose-description">Because reading ingredient lists shouldn't require a chemistry degree.</p>
            <div className="why-choose-features">
              <div className="feature-card">
                <h3>Fewer breakouts</h3>
                <p>Stop the cycle of buying a new product, breaking out, and starting over. Know what's safe before it touches your face.</p>
              </div>
              <div className="feature-card">
                <h3>Less money wasted</h3>
                <p>The average person throws away $180/year on products that don't work for their skin. One scan saves you from another expensive mistake.</p>
              </div>
              <div className="feature-card">
                <h3>Works anywhere you shop</h3>
                <p>In-store at Sephora, browsing Dermstore, or reading labels at Target — scan from your phone or your browser.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Paste Ingredients Section */}
        <section id="ingredients-checker" className="paste-ingredients-section">
          <div className="paste-ingredients-content">
            <h2 className="section-title">Try it now — paste an ingredient list</h2>
            <p className="paste-description">Copy the ingredients from any product page and paste them below. We'll flag the pore-cloggers instantly.</p>
            <div className="ingredients-input-container">
              <textarea 
                className="ingredients-textarea"
                placeholder="Paste ingredient list here (e.g., Water, Glycerin, Niacinamide, Hyaluronic Acid...)"
                rows="6"
                value={ingredients}
                onChange={(e) => setIngredients(e.target.value)}
              />
              <button className="analyze-button" onClick={handleAnalyze}>Analyze Ingredients</button>
              {result && (
                <div className="analysis-result">
                  <p className={result.isComedogenic ? 'result-warning' : 'result-positive'}>
                    {result.message}
                  </p>
                  {result.foundIngredients && result.foundIngredients.length > 0 && (
                    <ul className="ingredients-list">
                      {result.foundIngredients.map((ingredient, index) => (
                        <li key={index}>{ingredient}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="home-faq-section">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="home-faq-list">
            {homeFaqs.map((faq) => (
              <div className="faq-item" key={faq.question}>
                <h3>{faq.question}</h3>
                <p>{faq.answer}</p>
              </div>
            ))}
          </div>
        </section>
      </main>

      {/* Testimonials */}
      <section className="testimonials">
        <div className="testimonials-content">
          <h2 className="section-title">What our users are saying</h2>
          <div className="testimonial-cards">
            <div className="testimonial-card">
              <p className="quote">"I scanned a moisturizer I was about to buy and it flagged three pore-clogging ingredients. Saved me $45 and a breakout."</p>
              <p className="author">Sarah M.</p>
            </div>
            <div className="testimonial-card">
              <p className="quote">"My skin has been so much clearer since I started checking everything before I buy. I wish I had this years ago."</p>
              <p className="author">Jessica L.</p>
            </div>
            <div className="testimonial-card">
              <p className="quote">"I used to think my skin was just bad. Turns out I was using three products with coconut oil derivatives. This app caught all of them."</p>
              <p className="author">Emily K.</p>
            </div>
            <div className="testimonial-card">
              <p className="quote">"I use it every time I'm at Sephora. Just scan the label and I know in two seconds if it's safe. So easy."</p>
              <p className="author">Maria G.</p>
            </div>
            <div className="testimonial-card">
              <p className="quote">"The Chrome extension is clutch. I was shopping on Dermstore and it flagged a serum before I added it to my cart."</p>
              <p className="author">David R.</p>
            </div>
            <div className="testimonial-card">
              <p className="quote">"As someone with hormonal acne, every product matters. This takes the guesswork out completely."</p>
              <p className="author">Lisa T.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="app-footer">
        <div className="footer-content">
          <div className="footer-left">
            <h3 className="footer-logo">Acne Safe</h3>
            <a href="https://apps.apple.com/us/app/acne-safe-ingredient-scanner/id6747936538" target="_blank" rel="noopener noreferrer" className="footer-download-button" onClick={() => trackAppStoreCtaClick('footer')}>
              <img src="/download.png" alt="Download on App Store" className="footer-download-image" />
            </a>
          </div>
          
          <div className="footer-sections">
            <div className="footer-section">
              <h4 className="footer-section-title">Check Ingredients</h4>
              <ul className="footer-links">
                <li><Link to="/acne-safe-ingredient-checker" className="footer-link">Acne Safe Ingredient Checker</Link></li>
                <li><Link to="/pore-clogging-ingredient-checker" className="footer-link">Pore Clogging Checker</Link></li>
                <li><Link to="/comedogenic-ingredient-checker" className="footer-link">Comedogenic Checker</Link></li>
              </ul>
            </div>

            <div className="footer-section">
              <h4 className="footer-section-title">Legal</h4>
              <ul className="footer-links">
                <li><a href="/privacy.html" className="footer-link">Privacy Policy</a></li>
                <li><a href="/terms.html" className="footer-link">Terms of Service</a></li>
              </ul>
            </div>
            
            <div className="footer-section">
              <h4 className="footer-section-title">Company</h4>
              <ul className="footer-links">
                <li><button className="footer-link" style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.875rem' }}>Contact</button></li>
              </ul>
            </div>
          </div>
        </div>
        
        <div className="footer-bottom">
          <p className="footer-copyright">&copy; {new Date().getFullYear()} Acne Safe. All rights reserved.</p>
          <div className="footer-social">
            <a href="https://www.tiktok.com/@acnesafe" target="_blank" rel="noopener noreferrer" className="social-link">
              <img src="/tiktok-logo.png" alt="TikTok" className="social-icon" />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Home;
